<i18n>
  {
    "de": {
      "releaseNotes": "Release Notes",
      "version": "Version",
      "changeTypes": {
        "additions": "Neu hinzugefügt",
        "extensions": "Erweiterungen",
        "upgrades": "Aktualisierungen",
        "fixes": "Verbesserungen"
      }
    }
  }
  </i18n>

<template>
  <div class="c-version-number">
    <button type="button" class="button button--ghost" @click="showReleaseNotesModal">
      <img src="/icons/compass.svg" alt="Compass icon" />
      <span>{{ releases[0].version }}</span>
    </button>

    <Modal v-if="isModalOpen" :width="800" class="release-notes-modal" @close="onModalClose">
      <template #header>
        <h1>{{ $t('releaseNotes') }}</h1>
      </template>

      <template>
        <div v-for="release in releases" :key="release.version" class="release-block">
          <h2>{{ $t('version') }} {{ release.version }}</h2>
          <span v-if="release.date !== ''" class="version-date">
            ({{
              release.date
                | luxon({
                  input: { format: 'yyyy-MM-dd' },
                  output: { format: 'DDDD', locale: $i18n.locale },
                })
            }})
          </span>

          <div class="version-summary" v-html="release.summary" />

          <div v-for="(changeType, index) in changeTypes" :key="index">
            <div v-if="release.changes[changeType] && release.changes[changeType].length" class="change-type">
              <span class="change-type__title">{{ $t(`changeTypes.${changeType}`) }}</span>

              <div>
                <template v-if="release.changes[changeType].length">
                  <ul class="changes">
                    <li v-for="(change, indexChange) in release.changes[changeType]" :key="indexChange">
                      {{ change }}
                    </li>
                  </ul>
                </template>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import ModalMixin from '@/components/shared/ModalMixin.vue'

import Modal from '@/components/shared/Modal.vue'

export default {
  mixins: [
    //
    ModalMixin,
  ],

  components: {
    Modal,
  },

  data() {
    return {
      changeTypes: ['additions', 'extensions', 'upgrades', 'fixes'],
      releases: [
        // {
        //   version: 'Alpha',
        //   date: '',
        //   summary: '',
        //   changes: {
        //     additions: [],
        //     extensions: [],
        //     upgrades: [],
        //     fixes: [],
        //   },
        // },
        {
          version: '2.3.1',
          date: '2025-07-23',
          summary: 'Dieser Release behebt einige Probleme bei der Berechnung von Szenarien.',
          changes: {
            additions: [],
            extensions: [],
            upgrades: [],
            fixes: [],
          },
        },
        {
          version: '2.3.0',
          date: '2025-06-10',
          summary:
            'Dieser Release bringt einen neuen Import & Export Prozess für Liegenschaften, Massnahmen, Energieträger und Verbrauchsdaten. Zudem wurden einige Fehler behoben.',
          changes: {
            additions: [
              //
              'Neu kann der Anteil fossile Energie für Energieträger definiert werden.',
            ],
            extensions: [],
            upgrades: [
              'Die Import-Seite wurde neu gedacht und verbessert. Vier Aspekte können exportiert und wieder importiert werden: Liegenschaften, Szenarios, Verbrauchsdaten und Energieträger (inkl. Emissionsfaktoren)',
              'Die Liegenschaft-Stammdaten (initialer Zustand) und die geplante Massnahme (neu CAPEX-Planung genannt) können als Excel-Datei exportiert und importiert werden. Dies ermöglicht die Kontrolle, Bearbeitung und Erfassung der Stammdaten.',
              'Szenarios (inkl. CAPEX-Massnahmen) können als Excel-Datei exportiert und importiert werden. Dies ermöglicht die Kontrolle, Bearbeitung und Erfassung der Massnahmen.',
              'Energieträger und ihre Emissionsfaktoren können als Excel-Datei exportiert und importiert werden. Dies ermöglicht die Kontrolle, Bearbeitung und Erfassung der Energieträger.',
              'Verbrauchsdaten können als Excel-Datei exportiert und importiert werden. Dies ermöglicht die Kontrolle, Bearbeitung und Erfassung aller Verbrauchsdaten.',
              'Die Portfolio Resultate Export (CSV-Datei) wurde verbessert und kann neu auch als Excel-Datei exportiert werden. Die Resultate zeigen alle relevanten Eigenschaften und Kennzahlen der Liegenschaften im Portfolio und die Resultate alle Szenarios am Endjahr des Simulationsperiodes.',
            ],
            fixes: [],
          },
        },
        {
          version: '2.2.2',
          date: '2025-01-27',
          summary: 'Dieser Release beinhaltet kleinere Verbesserungen und Fehlerbehebungen.',
          changes: {
            additions: [],
            extensions: [],
            upgrades: [],
            fixes: [
              'Fehler wurden behoben, bei dem unbeheizte Gebäude falsch berechnet wurden.',
              'Fehler bei der Prüfung der Zulässigkeit von Erdwärmesonden wurden für alle Kantone behoben.',
            ],
          },
        },
        {
          version: '2.2.1',
          date: '2024-12-23',
          summary:
            'Dieser Release behebt Probleme beim Import der Verbrauchsdaten, der Eingabevalidierung und der Graphikdarstellung.',
          changes: {
            additions: [],
            extensions: [],
            upgrades: [],
            fixes: [
              'Ein Fehler bei der Darstellung der Graphik "Ausbau PV Best-Case" wurde behoben.',
              'Ein Fehler bei der Eingabevalidierung des Umsetzungsjahres von Massnahmen wurde behoben.',
              'Ein Fehler beim Import der Verbrauchsdaten wurde behoben.',
            ],
          },
        },
        {
          version: '2.2.0',
          date: '2024-10-24',
          summary:
            'Dieser Release bringt umfangreiche visuelle Verbesserungen und Neugestaltungen. Dazu gehören ein neues Layout der Benutzeroberfläche, verbesserte KPI-Darstellungen, die Integration einer Satellitenbildkarte und eine neue, umfassende Darstellung der Gebäudehistorie. Zusätzlich wurden einige Fehler behoben (z.B. bei der Berechnung von unbeheizten Gebäuden und Wärmepumpen).',
          changes: {
            additions: [
              'Eine Übersicht des aktuellen Gebäudezustands wurde in der Gebäudedetails-Seite hinzugefügt.',
              'Die CO₂-Vermeidungskosten aller Massnahmen wurden berechnet und in der Massnahmen-Ansicht der Gebäudedetails-Seite dargestellt, um die Effizienz der einzelnen Massnahmen zu visualisieren.',
              'Eine interaktive Liste der Massnahmen wurde in der Kostenübersicht (Absenkpfad > Investitionskosten > Übersicht) hinzugefügt, um die Kostenverteilung genauer zu visualisieren.',
              'Lebenszyklus & Massnahmen: Eine Neugestaltung der Sanierungshistorie wurde implementiert, um die Gebäudehistorie klar darzustellen.',
              'Alle Abschnitte der Gebäudedetails-Seite können nun zusammengeklappt werden.',
              'Eine Swisstopo Satellitenbildkartenschicht wurde hinzugefügt und wird standardmässig in der Portfolio-Übersicht und in der Gebäudedetails-Seite angezeigt. Die normale Kartenansicht ist immer noch verfügbar.',
              'Ein neues Seitenpanel-Layout wurde in der Gebäudedetails-Seite implementiert analog zu der Portfolio-Übersicht.',
            ],
            extensions: [
              'Zielpfade für Endenergie & Scope 1-3 wurden hinzugefügt und eine Grafik in der Einstellungen-Seite wurde implementiert, um eine Vorschau der Zielpfade darzustellen.',
            ],
            upgrades: [
              'Der Verbrauchsdaten-Abschnitt in der Gebäudedetails-Seite hat nun eine neue Gestaltung und wurde auf "Controlling" umbenannt.',
              'Die Abschnitte für Energie-, Emissions- und PV-KPIs in der Gebäudedetails-Seite wurden neugestaltet und verbessert.',
              'Der Schriftart wurde geändert und die grafische Darstellung wurde in vielen Komponenten verbessert. Unter anderem wurde die Tabelle-Ansicht der Gebäudeliste in der Portfolio-Übersicht verbessert.',
              'Benutzerdefinierte Kategorien sind nun in einem separaten Bereich in der Gebäudedetails-Seite dargestellt.',
            ],
            fixes: [
              'Ein Fehler wurde behoben, bei dem die Zulässigkeit von Erdwärmesonden in Bern nicht korrekt abgefragt wurde.',
              'Ein Fehler wurde behoben, bei dem Duplizieren von Szenarien mit Massnahmen nicht funktioniert.',
              'Ein Fehler wurde behoben, bei dem unbeheizte Gebäude und Wärmepumpen falsch berechnet wurden. Nun werden sie korrekt simuliert und in den Endenergie-KPIs und Diagrammen richtig dargestellt.',
              'Ein Fehler wurde behoben, bei dem zukünftig geplante Massnahmen als umgesetzte markiert werden könnten.',
            ],
          },
        },
        {
          version: '2.1.3',
          date: '2024-08-09',
          summary: 'Dieser Release behebt Probleme bei der Bearbeitung von Massnahmen, Szenarien und PV-Anlagen.',
          changes: {
            additions: [],
            extensions: [],
            upgrades: [],
            fixes: [],
          },
        },
        {
          version: '2.1.2',
          date: '2024-07-17',
          summary: 'Dieser Release behebt ein Problem bei der Bearbeitung von Szenarien.',
          changes: {
            additions: [],
            extensions: [],
            upgrades: [],
            fixes: [],
          },
        },
        {
          version: '2.1.1',
          date: '2024-07-03',
          summary: 'Dieser Release beinhaltet kleinere Verbesserungen und Fehlerbehebungen.',
          changes: {
            additions: [],
            extensions: [],
            upgrades: [],
            fixes: [],
          },
        },
        {
          version: '2.1.0',
          date: '2024-06-24',
          summary: 'Dieser Release bringt zahlreiche Verbesserungen und Erweiterungen.',
          changes: {
            additions: [],
            extensions: [
              'Es gibt neu eine Möglichkeit nicht unterkellerte Liegenschaften zu markieren, so dass keine Massnahmen dafür erstellt werden.',
              'Neu gibt es ein Bemerkungsfeld für jede Heizung einer Liegenschaft.',
              'Neu zeigen wir die REIDA-Benchmarks vom Jahr 2023 für CO₂-Emissionen und Energieverbrauch in den CO₂mpass Grafiken.',
              'Die Wirtschaftlichkeitsberechnung kann nun auf selbstdefinierten Tarifen basiert werden (siehe Elektrizitätsversorgung und Preise).',
              'Neu können Szenarien dupliziert werden, so dass auch alle Szenario editierten Massnahmen kopiert werden.',
            ],
            upgrades: [
              'Der CO₂mpass fokussiert neu auf Endenergie. Primärenergie ist aber weiterhin verfügbar.',
              'Die Darstellung der Liegenschaftsübersicht wurde überarbeitet.',
              'Kategorien werden nun in den Einstellungen sortiert.',
              'Im Streudiagramm (Bubble Chart) wird neu Endenergie angezeigt und jegliche Anteile von mieterseitigen Energie und Emissionen werden nicht angezeigt.',
              'Die Einheiten der Emissionen werden neu komplett angegeben (zum Beispiel: kg CO₂e). Zudem wurden die Einheiten in gewissen Tabellen in der Titelzeile angegeben, was zu besserer Übersicht führt.',
              'Scope 1+2+3 heisst neu Scope 1-3 und Scope 1+2 heisst neu Scope 1-2.',
              'Einspeisung wird neu Rückspeisung genannt.',
              'Die Einheit von Solareinstrahlung wurde angepasst (kWh_sol) um eine klare Trennung vom Solarertrag zu garantieren.',
              'In den Einstellungen unter Heizungstypen werden Boiler neu nur noch Heizung genannt.',
              'Netzstrom wurde ersetzt durch Standard-Stromprodukt. Die Energieträgertabelle wurde aufgeteilt in Stromprodukte und Energieträger. Weiter wurde Netzstrom dynamisch ersetzt durch besser verständliche Begriffe.',
              'Betriebskosten für eine PV-Anlage in neuen Portfolios werden ab heute auf 3 Rp./kWh angenommen, nicht mehr 5 Rp./kWh. (für eine Anpassung Ihres aktuellen Portfolios bitte melden Sie sich beim CO₂mpass Support),',
              'Die Informationen im PV-Varianten-Vergleich wurden verbessert.',
              'Neu können selbstdefinierte Heizungstypen mit dem Excel Import importiert werden.',
            ],
            fixes: [
              'Der Modellwert von Wärmenetzten wird nun korrekt berechnet.',
              'Die manuelle Kalibrierung von Heizungen wird nun immer angewendet.',
              'Die Darstellung der Grafiken zu Photovoltaik und Elektrizität im Absenkpfad wurden verbessert und korrigiert.',
              'Berechnungen sind nun stabiler, da einige Grenzfälle nun korrekt behandelt werden.',
              'Bezeichnung der Liegenschaftsanzahl wurde korrigiert.',
              'Die Darstellung einiger Tabellen und Auswahlen wurde überarbeitet, um einen Grenzfall des Renderings zu korrigieren.',
              'Ein seltener Fehler bei der Abfrage von Dachflächen wurde behoben.',
              'Eine Inkonsistenz in der Formatierung der Zahlen wurde beim Anteil nutzbar (PV / Dachflächen) behoben.',
              'Ein Problem bei einem Grenzfall der Darstellung der Quelle von Emissionsfaktoren wurde behoben.',
              'Der Benchmark Bauteilalter wurde für die Kellerdecke von 0.35 auf 0.45 angehoben.',
              'Im Streudiagramm (Bubble Chart) des CO₂mpass Reports wurden die Informationen im Popup korrigiert.',
              'Grafiken im Absenkpfad mit Liegenschaften in der x-Achse werden nun auf die wichtigsten beschränkt, wenn zu viele Gebäude dargestellt werden müssten.',
            ],
          },
        },
        {
          version: '2.0.0',
          date: '2024-03-22',
          summary:
            '<p class="section">CO<sub>2</sub>mpass-Report nach REIDA</p> <p> Das Reporting nach REIDA ist neu automatisiert möglich. Zu diesem Zweck wurde ein Reporting-Reiter eingeführt, mit dem jederzeit die Berichte der verschiedenen Jahre erstellt und eingesehen werden können. </p> <ul> <li>Nachfolgende umweltrelevanten Kennzahlen werden ausgegeben. AMAS umweltrelevante Kennzahlen sind dabei kursiv geschrieben. <ul> <li><i>Abdeckungsgrad</i></li> <li><i>CO₂e Emissionen (Scope 1-2)</i></li> <li><i>CO₂e Emissionsintensität</i></li> <li><i>Energieverbrauch (Endenergie)</i></li> <li><i>Energieintensität</i></li> <li><i>Anteil fossile Energie</i></li> <li>Anteil erneuerbare Energie</li> </ul> </li> <li> In der Portfolioübersicht kann nach dem Reporting-Jahr und dem Liegenschaftsstatus gefiltert werden. </li> <li> Jedem Standard-Energieträger des CO₂mpass ist ein REIDA-Äquivalent entsprechend der REIDA-Methodik zugeordnet. Bitte beachten Sie, dass für die Erstellung eines Reports jedem selbsterstellten Energieträger ein REIDA-äquivalenter Energieträger zugewiesen werden muss! </li> </ul>  <p class="section">Liegenschaftsrollen</p>  <p>Liegenschaften können in einer Lizenzperiode neu in drei Rollen eingeteilt werden:</p>  <table> <tr> <td class="keyword">Planer</td> <td> Planer-Liegenschaften haben vollen und uneingeschränkten Zugang zu sämtlichen Funktionen des CO₂mpass für Planung und Reporting. </td> </tr> <tr> <td class="keyword">Reporter</td> <td> <p> Reporter-Liegenschaften können für das Reporting verwendet werden, wobei die bestehende Planung und Szenarien weiterhin einsehbar bleiben. Der Funktionsumfang ist in der Liegenschafts-Rolle „Reporter“ spezifisch eingeschränkt nutzbar. Das aktive Bearbeiten folgender Funktionen ist möglich: </p> <ul> <li>Erfassung von Verbrauchsdaten</li> <li>Verwaltung von Verbrauchsdaten</li> <li> Dokumentation umgesetzter Massnahmen (z.B. Renovations- oder Optimierungsmassnahmen) </li> </ul> <p> Sämtliche Auswertungen bereits geplanter Sanierungsmassnahmen und erfasster Daten werden sowohl auf Liegenschafts- als auch auf Portfolioebene mit einer Betrachtung der Vergangenheit und der Zukunft dargestellt. In der Rolle „Reporter“ ist die Möglichkeit zur Erstellung und Bearbeitung geplanter Massnahmen der einzelnen Liegenschaften nicht möglich. Diese Funktionalitäten sind ausschliesslich in der Rolle „Planer“ verfügbar. </p> </td> </tr> <tr> <td class="keyword">Archiviert</td> <td> Die Rolle „Archiviert“ ermöglicht, Liegenschaften zu archivieren, welche weder aktiv bewirtschaftet noch fürs Reporting eingesetzt werden. Im archivierten Zustand sind keine Funktionen verfügbar. Archivierte Liegenschaften tauchen nicht in Reportings auf. Es ist jederzeit möglich, archivierte Liegenschaften zu aktivieren und weiter zu bearbeiten. </td> </tr> </table>  <p class="section">Vorgehen beim Wechsel der Liegenschaftsrollen:</p>  <p> Die gewünschte Rolle wird zum Start der jährlichen Nutzungsdauer von der Nutzerin für jede Liegenschaft bekannt gegeben. Ein Wechsel der Rolle von „Reporter“ zu „Planer“, „Archiviert“ zu „Reporter“ oder „Planer“ ist während der Nutzungsdauer für jede Liegenschaft jederzeit direkt möglich, ebenso wie das Hinzufügen von zusätzlichen Liegenschaften. </p>  <p> Der Rollenwechsel von „Planer“ zu „Reporter“ oder „Archiviert“ sowie von Reporter zu „Archiviert“ können jederzeit geplant werden, werden aber erst zu Beginn der neuen Vertragsperiode umgesetzt. Im Lizenz-Reiter werden alle ausstehenden Rollenänderungen aufgelistet. </p>  <ul> <li> Für die Übersicht über den aktuellen Stand und die Historie der Rollen wurde der Reiter „Lizenz“ hinzugefügt. </li> <li> In der Portfolioübersicht haben Sie die Möglichkeit, nach diesen Rollen zu filtern. In der Standardeinstellung werden nur die Planer und die Reporter angezeigt. Durch die Verwendung des Filters können auch die archivierten Liegenschaften angezeigt werden. </li> <li> Bitte beachten Sie dabei, dass <ul> <li>... archivierte Liegenschaften nicht neu berechnet werden.</li> <li>... Reporter- und archivierte Liegenschaften im Liegenschaftsimport ignoriert werden.</li> <li>... archivierte Liegenschaften im Verbrauchsdatenimport ignoriert werden.</li> </ul> </li> </ul>  <p class="section">Weitere Anpassungen</p> ',
          changes: {
            additions: [],
            extensions: [
              'Beim Absenkpfad wurden folgende Referenzpfade hinzugefügt: BAFU Pacta 2022 bei Scope 1 und CRREM 2023 (Büro und Mehrfamilienhaus) bei Scope 1-3.',
              'Neu können zusätzlich die vermietbaren Flächen (VMF) angeben werden.',
            ],
            upgrades: [
              'Die Benutzeroberfläche der Szenario-Seite sowie der Filterung wurde überarbeitet.',
              'Die Verbrauchsdaten werden neu direkt in den Details zu Liegenschaften dargestellt. Zudem wird der Stichmonat bildlich dargestellt.',
              'Elektrizität wurde in zwei Zeilen aufgeteilt: Stromnetz und Photovoltaik.',
            ],
            fixes: [
              'Die Portfolioübersicht wird nun zuverlässig geladen.',
              'Eine externe Schnittstelle hat sich geändert und der Compass wurde dementsprechend angepasst.',
              'Der Netzstrom (dynamisch) und der Netzstrom (dynamisch mit PV) können jetzt über die Importdatei für Verbrauchsdaten importiert werden.',
              'Sofern Verbrauchsdaten vorliegen, werden diese nun anteilig auf jede Heizung mit dem entsprechenden Energieträger verteilt.',
              'Verbrauchsdaten werden nun korrekt sortiert und das vorgeschlagene Jahr beim Hinzufügen von Verbrauchsdatenwerten wurde optimiert.',
              'Gewisse Auswahlfelder wurden vergrössert um die Auswahl zu vereinfachen.',
              'Die Reihenfolge von Liegenschaften spielt nun im XLSX-Import keine Rolle mehr.',
            ],
          },
        },
        {
          version: '1.12.3',
          date: '2024-01-19',
          summary: 'Dieser Release behebt ein Problem beim Importieren von Verbrauchsdaten.',
          changes: {
            additions: [],
            extensions: [],
            upgrades: [],
            fixes: [],
          },
        },
        {
          version: '1.12.2',
          date: '2023-12-22',
          summary:
            'Dieser Release behebt ein Problem beim Erstellen und Importieren von Liegenschaften. Eine Änderung der Schnittstelle einer externen Informationsquelle hat zu Fehlern geführt, die jetzt behoben sind.',
          changes: {
            additions: [],
            extensions: [],
            upgrades: [],
            fixes: [],
          },
        },
        {
          version: '1.12.1',
          date: '2023-12-20',
          summary:
            'Dieser Release verbessert die Performance der Liegenschaftsliste, der Kennzahlen und der Liegenschaftsdetails.',
          changes: {
            additions: [],
            extensions: [],
            upgrades: [],
            fixes: [
              'Der Energieträger «PV-Strom mit Nutzung Standard» wird bei neu erstellten Portfolios dem Scope 2 zugeordnet. Zudem wurde beim Energieträger PV-Strom und Netzstrom eine Nutzung Mieter hinzugefügt, welche dem Scope 3 zugeordnet ist.',
              'Beim Verbrauchsdatenimport können nun alle in den Einstellungen definierten Energieträger angegeben werden. Somit können nun auch selbst definierte Energieträger verwendet werden.',
              'Das Export-CSV wird nun so erstellt, dass es mit Strichpunkten in der Beschreibung umgehen kann.',
              'Kennzahlen werden nun auch auf kleinen Bildschirmen dargestellt.',
              'Photovoltaikanlagen: Neu können manuell dimensionierten Anlagen ohne «Installierte Leistung» nicht mehr erfasst werden. Die minimale «Installierte Leistung» beträgt neu 0.001 kWp.',
              'In einigen Grafiken wird neu anstelle einer Zahl die Liegenschaftsbezeichnung angezeigt.',
              'Bei gewissen Löschvorgängen wird nun erst gefragt, ob man das wirklich möchte.',
              'Ein Problem mit dem Szenario Dropdown wurde bei Firefox behoben.',
            ],
          },
        },
        {
          version: '1.12.0',
          date: '2023-11-10',
          summary:
            'Die neuen Updates ermöglichen es, spezifische Stromenergieträgerfaktoren für Mieter oder Einspeisung zu definieren, und einige Faktoren wie Scope 1-2 und Primärenergie (erneuerbar) werden nun automatisch abgeleitet. Außerdem wurden Verbesserungen in der PV-Anlagenplanung und Korrekturen bei Heizungsemissionsfaktoren vorgenommen.',
          changes: {
            additions: [],
            extensions: [
              'Neu können Faktoren von Stromenergieträgern spezifisch für Mieter oder Einspeisung definiert werden. Dies erlaubt es z.B. Mieterstrom im Scope 3 abzubilden.',
            ],
            upgrades: [
              'Faktoren für Scope 1-2, Scope 1-3 und Primärenergie (erneuerbar) können neu nicht mehr bearbeitet werden. Sie werden aus den anderen Faktoren abgeleitet (siehe Einstellungen/Energieträger).',
              'Der Energieträger Netzeinspeisung wurde entfernt und durch PV-Strom mit der Nutzung Einspeisung ersetzt.',
              'Neu werden Allgemeinstrom und Mieterstrom auch durch Verbrauchsdaten kalibriert.',
              'Neu ist es möglich zu definieren, ob ein Zusammenschluss zum Eigenverbrauch (ZEV) existiert. PV-Strom wird auch für Mieterstrom verwendet, wenn ZEV aktiviert ist (siehe Liegenschaftsebene, Zusammenfassung/Elektrizität).',
              'Für die Amortisationszeit von PV-Anlagen wird nun ihre Lebensdauer verwendet (siehe Einstellungen/Lebensdauer).',
              'Emissionsfaktoren können nicht mehr in den einzelnen Liegenschaften geändert werden. Ebenso werden Emissionsfaktoren für Fernwärmenetze nicht mehr aus dem GIS abgerufen (siehe Liegenschaftsebene, Zusammenfassung/Heizung).',
              'Die vier Kategorien (Beleuchtung, Geräte, Lüftung, Prozessanlagen) von Mieterstrom wurden nun zu einer Kategorie zusammengefasst (siehe Liegenschaftsebene, Zusammenfassung/Energiebezugsfläche und Nutzung).',
            ],
            fixes: [
              'Anstelle einer leeren Massnahme erscheint nun eine Warnung, falls die automatisch PV-Anlagen Planung keine passenden Flächen findet.',
              'Heizungen, deren Emissionsfaktoren fälschlicherweise überschrieben waren, wurden korrigiert.',
              'In gewissen Grafiken wird die Liegenschaftsbezeichnung anstelle einer Zahl angezeigt.',
              'In der Zuverlässigkeit Wärmenutzung wird langer Text nun korrekt behandelt.',
              'Bei den Werten von Verbrauchsdaten gibt es nun eine Titelzeile.',
              'Die Platzierung der Infobox wurde an gewissen Stellen verbessert.',
              'Die Bezeichnung und Erklärung in Stromnetzdefinition wurden angepasst (siehe Liegenschaftsebene > Zusammenfassung > Elektrizität) und die Stromnetzmassnahme.',
              'Ein Problem mit der Darstellung der Stromproduktionszeile ist behoben.',
            ],
          },
        },

        {
          version: '1.11.2',
          date: '2023-08-30',
          summary:
            'Dieser Release erweitert die Möglichkeiten bei der Konfiguration von Photovoltaik und ermöglicht es Emissionsfaktoren via Excel-File zu importieren.',
          changes: {
            additions: [],
            extensions: [
              'Neu können Emissionsfaktoren via XLSX importiert werden.',
              'Neu kann man für Dachflächen einen globalen Anteil festlegen und für Fassadenflächen lokal den zu verwendenden Anteil bestimmen. Weiter bedeutet ein leeres Deckungsziel neu, dass so viele Flächen, wie möglich belegt werden.',
              'Die Seitentitel enthalten nun mehr Kontext für mehr Übersicht in den Browser-Tabs.',
            ],
            upgrades: [
              'Dachflächen und, wo nötig, Parzellen und Grundbuchdaten werden im Verlaufe der nächsten Wochen mit den aktuellsten öffentlich verfügbaren Daten aktualisiert. Bestehende Berechnungen bleiben erhalten, wenn aber an Portfolios gearbeitet wird, wird mit den aktualisierten Daten gerechnet.',
              'Wärmenetzeffizienz wird nun über eine Infobox erklärt.',
            ],
            fixes: [
              'Liegenschaften werden nur noch bei Bedarf neu geladen.',
              'Die Flächenberechnung der Parzellen ist verbessert worden.',
              'Ein Problem wurde behoben, bei dem das Auslassen der Angabe von Raumwärme- oder Brauchwarmwasseranteilen zu Fehlern führte. Zukünftig ist diese Eingabe nicht mehr möglich.',
              'Bei der Erstellung von Verbrauchsdatenpunkten wurde das Auswahlproblem behoben.',
              'Benutzerdefinierte Kategorien (z.B. Teilportfolios) können wieder entfernt werden.',
            ],
          },
        },
        {
          version: '1.11.1',
          date: '2023-07-28',
          summary:
            'Dieser Release bringt umfassende Verbesserungen und Erweiterungen hinsichtlich des Designs der Energieträger-Übersicht, der Liegenschaftsansicht und der Emissionsfaktoren mit sich, wie zum Beispiel Scope 3 Emissionen.',
          changes: {
            additions: [],
            extensions: [
              'Der CSV-Export der Liegenschaften enthält nun alle Faktoren (z.B. fossile Primärenergie oder Scope 3 Emissionen).',
              'Im Absenkpfad wird neu bei der fossilen Primärenergie auch der Anteil an der totalen Primärenergie in Prozent geplottet.',
              'Die Übersicht über die Auswirkungen einer Massnahme enthält nun alle Faktoren (z.B. fossile Primärenergie oder Scope 3 Emissionen).',
              'Neu wird der Identifikator von Emissionsfaktoren angezeigt, damit diese in Importfiles korrekt referenziert werden können.',
            ],
            upgrades: [
              'Bei Elektrizität und der Stromnetzmassnahme wurden die Emissionsfaktoren entfernt, da sie ohnehin nicht angezeigt wurden. An verschiedenen Stellen, wo Faktoren dargestellt werden, wurde die Ansicht überarbeitet, um eine verbesserte Darstellung zu gewährleisten.',
              'Die Darstellung einiger Komponenten wurde überarbeitet, die wichtigsten sind: Die Energieträgerübersicht und der Variantenvergleich Photovoltaik.',
            ],
            fixes: [
              'Ein Grenzfall bei vielen definierten Jahren in der Energieträgerübersicht wurde behoben.',
              'Ein Problem beim Erstellen von Jahren in der Energieträgerübersicht wurde behoben.',
              'Bei der Eingabe von Zahlen wird ein Spezialfall nun korrekt validiert.',
              'Die Probleme mit EWS-Abfragen der Kantone ZH / LU wurden behoben.',
              'Ein Problem mit der Liegenschaftsidentifikation wurde behoben.',
              'Fehlende Emissionsfaktoren werden nun korrekt behandelt. (sie werden als 0 interpretiert)',
            ],
          },
        },
        {
          version: '1.11.0',
          date: '2023-07-07',
          summary:
            'Dieser Release führt verschiedene neue Faktoren ein, wie zum Beispiel "Scope 3 EF". Zudem ermöglicht es, diese Faktoren im Laufe der Jahre anzupassen.',
          changes: {
            additions: [
              'Hinzugefügte Faktoren: "Scope 2 EF", "Scope 3 EF", "Scope 1-3 EF", "PEF fossil", "PEF erneuerbar". Die korrekte Berechnung der neuen Emissionen wird erst angezeigt, wenn das gesamte Portfolio neu berechnet wurde.',
              'Die neuen Emissionsberechnungen werden in den Kennzahlen der Portfolio-Übersicht, dem Absenkpfad und den Liegenschaftsdetails angezeigt.',
              'Es ist nun möglich, Faktoren im Laufe der Jahre anzupassen.',
            ],
            extensions: [],
            upgrades: [],
            fixes: [
              'Fehlerhafte Bezeichnungen der Emissionen wurden korrigiert.',
              'Die Anzeige der Grundstücksfläche wurde korrigiert.',
              'Ein Problem bei der Erstellung neuer Benutzer wurde behoben.',
              'Ein Problem mit einem Eingabefeld wurde behoben.',
            ],
          },
        },
        {
          version: '1.10.2',
          date: '2023-06-15',
          summary: 'Dieser Release beinhaltet Bugfixes.',
          changes: {
            additions: [],
            extensions: [],
            upgrades: [],
            fixes: [
              'Bei Anpassungen der Heizungstypen wird nun das gesamte Portfolio simuliert.',
              'Ein Spezialfall wurde behoben, der den CSV-Export des Portfolios verhinderte.',
            ],
          },
        },
        {
          version: '1.10.1',
          date: '2023-05-17',
          summary:
            'Dieser Release beinhaltet eine verbesserte PV-Potenzial-Berechnung und zusätzliche Darstellungen zu Stromproduktion und installierter Nennleistung.',
          changes: {
            additions: [],
            extensions: [
              'Eine Änderung wurde vorgenommen bezüglich der Ermittlung des PV (Photovoltaik)-Potenzials für Dach- und Fassadenflächen auf einem Grundstück. Anstatt alle Dach- und Fassadenflächen zu berücksichtigen, werden nun nur noch diejenigen Gruppen berücksichtigt, die eine Adresse/EGID (GWR-Eintrag) enthalten. Um zusätzliche Dachgruppen zu aktivieren, kann ein Dach zur Gruppierung hinzugefügt werden. Es besteht auch die Möglichkeit, bestimmte Dachflächen beim Gruppieren zu ignorieren, indem man sie sperrt.',
              'Im Absenkpfad für den Bereich Elektrizität gibt es zwei neue Funktionen. Nun wird die absolute Stromproduktion und die Entwicklung der Nennleistung (kWp) über die Jahre dargestellt. Bitte beachten Sie, dass die korrekte Anzeige der Nennleistung erst erfolgt, nachdem das gesamte Portfolio neu berechnet wurde.',
            ],
            upgrades: [],
            fixes: ['Das Kuchendiagramm der Wärmeerzeugung zeigt nun eine korrekte Legende an.'],
          },
        },
        {
          version: '1.10.0',
          date: '2023-05-11',
          summary:
            'Dieser Release umfasst Überarbeitungen der Energieträger, Heizungstypen und Stromnetzen, sowie diverse Fehlerbehebungen und Verbesserungen.',
          changes: {
            additions: [
              'Administratoren haben jetzt die Möglichkeit, mit einer einzigen Aktion mehrere Liegenschaften zu löschen.',
            ],
            extensions: [
              'Die Kennzahlen zeigen nun zusätzlich die Stromproduktion an.',
              'Es gab eine Überarbeitung der Energieträger. Jetzt finden Sie alle Einstellungen zu Energieträgern im Bereich "Energieträger" innerhalb der Portfolioeinstellungen. Das Überschreiben von Energieträgern und die Erstellung von Stromnetzen auf Liegenschaftsebene wird nun vermieden. Stattdessen werden spezifische Energieträger neu zentral auf Portfolioebene verwaltet.',
              'Die Heizungstypen wurden überarbeitet. Alle Einstellungen zu Heizungstypen finden Sie nun im Bereich "Heizungstypen" innerhalb der Portfolioeinstellungen. Jeder Heizungstyp verwendet nun einen eindeutig definierten Energieträger.',
              'In der Zusammenfassung der Liegenschaft wurde der Eintrag "Photovoltaik" durch "Elektrizität" ersetzt. Es ist neu möglich, die initial verwendeten Stromnetzwerke für den Netzstrom, den PV-Strom und die Netzeinspeisung zu definieren. Außerdem kann die Stromnetzmassnahme verwendet werden, um eines oder alle Stromnetzwerke durch neue zu ersetzen. Im Zusammenhang damit gibt es auch neue dynamische Stromenergieträger (dynamischer Netzstrom, dynamischer Netzstrom mit PV). Wenn ein Heizungstyp diese Energieträger verwendet, werden die Emissionen entsprechend der jeweils ausgewählten Stromnetzwerke berechnet.',
            ],
            upgrades: [],
            fixes: [
              'Die Auswahl der Rolle wurde auf User und Manager vereinfacht.',
              'Der Excel-Import wurde verbessert, um eine bessere Konsistenz mit der Benutzeroberfläche zu gewährleisten.',
              'Ein Problem wurde behoben, bei dem das manuelle Setzen des Wärmebedarfs dazu führte, dass das Baujahr nicht angezeigt wurde.',
              'Ein Fehler wurde behoben, bei dem die Sortierung nach Gemeinde in bestimmten Fällen nicht korrekt funktionierte.',
              'Massnahmen im gleichen Jahr werden jetzt konsistent behandelt.',
              'Es wird nun verhindert, dass die gesamte Energiebedarfsfläche (EBF) einer Liegenschaft den Wert 0 haben kann. Einzelne Nutzungen können jedoch immer noch eine EBF von 0 haben, sofern andere Zonen vorhanden sind, die eine EBF größer als 0 aufweisen.',
              'Ein Problem mit der Anzeige der Übersichtskarte des Portfolios wurde behoben. Das Scrollen führt nicht mehr zu unbeabsichtigten Sprüngen.',
            ],
          },
        },
        {
          version: '1.9.6',
          date: '2023-03-15',
          summary: 'Dieser Release führt eine Warteschleife für Berechnungen ein und verbessert die Stabilität.',
          changes: {
            additions: [
              'Einige länger dauernde Berechnungen werden neu in einer Warteschleife ausgeführt. ' +
                'So werden Wartezeiten reduziert und die Benutzerfreundlichkeit verbessert. ' +
                'Es wird neu angezeigt, wie viele und welche Gebäude noch verarbeitet werden müssen.',
            ],
            extensions: [],
            upgrades: [],
            fixes: [
              'Portfolio JSON-Imports werden neu asynchron ausgeführt.',
              'Beim Wechsel zur Administrationsseite tritt kein Fehler mehr auf.',
              'EWS-Abfragen für die Kantone SG und AI wurden korrigiert.',
              'Ein Fehler bei gleichnamigen Kategorien wurde behoben.',
              'Der Liegenschaftsexport enthält nun auch geographische Breite und Länge, wenn vorhanden.',
            ],
          },
        },
        {
          version: '1.9.5',
          date: '2023-02-22',
          summary: 'Dies ist ein Bugfix-Release.',
          changes: {
            additions: [],
            extensions: [],
            upgrades: [],
            fixes: ['Das Löschen von Liegenschaften ist wieder möglich.'],
          },
        },
        {
          version: '1.9.4',
          date: '2023-02-17',
          summary: 'Dieser Release verbessert die Stabilität und aktualisiert die Anbindung an externe Quellen.',
          changes: {
            additions: [],
            extensions: ['Es gibt nun die Möglichkeit, externe Quellen für mehrere Liegenschaften zu aktualisieren.'],
            upgrades: [],
            fixes: [
              'Eine Simulationsproblem wurde behoben, bei dem PV Anlagen falsch abgefragt werden.',
              'Simulationen sind nun stabiler, weil flexibler mit fehlenden Stromdaten umgegangen wird.',
              'Unsere Abfrage von Zulässigkeit Wärmenutzung wurde, wo nötig, an die neuen externen Schnittstellen der Kantone angepasst. Die Abfrage ist nun wieder möglich für AR, BE, BL, SO und UR.',
              'Einige Rechtschreibfehler wurden behoben.',
            ],
          },
        },
        {
          version: '1.9.3',
          date: '2023-02-03',
          summary: 'Dieser Release verbessert die Stabilität und aktualisiert die Strom-Preise.',
          changes: {
            additions: [],
            extensions: [],
            upgrades: ['Die neuesten Strom-Preislisten sind hinterlegt. (Stand 19.10.2022)'],
            fixes: [
              'Die Darstellung von Dezimalstellen wurde korrigiert.',
              'Neu wird überprüft, dass die Reihenfolge der Massnahmen korrekt verwendet wird.',
              'Bei Simulationen wird nun mit der Spezifikation der Reihenfolge der Massnahmen gearbeitet, was zu mehr Stabilität führt.',
            ],
          },
        },
        {
          version: '1.9.2',
          date: '2023-01-20',
          summary:
            'In diesem Release wurde die Stabilität verbessert und die Ladezeit der Portfolioübersicht verkürzt.',
          changes: {
            additions: [],
            extensions: [],
            upgrades: [
              'Verbesserte Ladezeit bei der Portfolioübersicht',
              'Überarbeitung des Designs von Release Notes',
              'Strukturelle Verbesserungen am Quellcode',
            ],
            fixes: [
              'Behebung eines Grenzfalles, der das Öffnen von Verbrauchsdaten verhindert',
              'Berücksichtigung des aktiven Zeitraums von Gebäuden beim Szenarienvergleich von Investitionskosten',
              'Korrektur der Kalibrierungsberechnung in einem Spezialfall',
            ],
          },
        },
        {
          version: '1.9.1',
          date: '2022-12-01',
          summary:
            'In diesem Release wurden viele kleine Korrekturen vorgenommen und die Benutzerfreundlichkeit verbessert.',
          changes: {
            additions: ['Möglichkeit für Portfolio Manager einzelne Liegenschaften als JSON-Datei zu importieren'],
            extensions: [],
            upgrades: ['Link zu Verwaltung im Untermenü hinzugefügt', 'Präzisere Infobox für die Anzahl der Geschosse'],
            fixes: [
              'Unterstützung von Heizungen, die nicht benutzt werden',
              'Editoren können den Standort anpassen',
              'Korrektur des CSV-Exports (Reihenfolge der Titelzeile)',
              'Information und optimierter Workflow zum Ändern vom Heizwärmebedarf',
              'Behebung der Überlagerung von manuellen Stromnetzwerken im Gebäude und manuellen Stromnetzwerken im Portfolio',
              'Korrigiertes Verhalten bei der Reihenfolge von Massnahmen betreffend Stromnetzwerken',
              'Messdaten vor dem Simulationsstart werden nun berücksichtigt',
              'Behebung des Abfragefehlers bei nur anteilmässig nutzbarer PV-Dachfläche',
            ],
          },
        },
        {
          version: '1.9.0',
          date: '2022-11-11',
          summary:
            'In diesem Release wurde die Stabilität verbessert, sowie der Import grösserer und komplexerer Portfolios ermöglicht. Dazu wurde die Codebasis massgeblich überarbeitet.',
          changes: {
            additions: ['Import von Kategorisierung für Liegenschaften aus XLSX-Import-Dateien'],
            extensions: [],
            upgrades: [
              'Verbesserte Modellierung der Software Objekttypen im Frontend Code',
              'Überarbeitung der Priorisierung von mehreren Zonen bei GIS-Abfragen',
              'Darstellung von Ungenauigkeiten bei GIS-Abfragen im Kanton Schwyz',
              'Unterstützung von Imports grosser Portfolios durch Verarbeitung im Hintergrund',
            ],
            fixes: [
              'Behandlung von Grenzfällen bezüglich Wärmeverlusten von Gebäudekomponenten',
              'Unterstützung von Zahlen in Textfeldern in XLSX-Import-Dateien',
              'Korrektur von GIS-Abfrage für den Kanton Luzern',
              'Behebung von Problem beim Import von Photovoltaikanlagen',
              'Nutzerfreundliche Behandlung von Zugriffsversuchen ausserhalb der Nutzer-Berechtigungen',
              'Behebung fehlerhafte Berechnung (Raumwärme)',
              'Unterstützung der Neuberechnung von Wärmenetzen und Wärmequellen trotz Abfragefehler',
            ],
          },
        },
        {
          version: '1.8.1b',
          date: '2022-10-12',
          summary: 'Dieser Release korrigiert Fehler und erhöht allgemein die Stabilität des CO₂mpass.',
          changes: {
            fixes: ['Umgang mit fehlenden Wärmeerzeuger- und Wärmepumpentypen', 'Umgang mit unbeheizten Gebäuden'],
          },
        },
        {
          version: '1.8.1',
          date: '2022-09-20',
          summary:
            'Dieser Release beinhaltet eine Vielzahl an Fehlerbehebungen und verbessert die allgemeine Robustheit des CO₂mpass.',
          changes: {
            additions: [
              'Release Notes zur Erläuterung der Änderungen',
              'Konsolidierung von U-Werten und Investitionskosten in Renovationsstandards',
              'Nutzungsdatennorm Portfolio Einstellung',
              'Massnahme zur Änderung des Stromnetzes',
              'Alternative Berechnung der Kalibrierungsfaktoren',
              'Eigene Energieträger und Heizungstypen',
            ],
            extensions: [
              'Auflistung fehlerhafter Liegenschaften im Reporting',
              'Liegenschaften Identifikation in einigen Report Exports',
              'Investitionskosten pro Jahr im Reporting',
              'Beibehaltung der Kalibrierungsfaktoren nach Massnahmen an der Gebäudehülle',
            ],
            upgrades: ['Investitionskosten einiger Renovationsstandards'],
            fixes: ['Fehlende Solarthermie im Absenkpfad'],
          },
        },
        {
          version: '1.8.0',
          date: '2022-08-01',
          summary:
            'Nebst einiger Fehlerbehebungen und Stabilitätsverbesserungen, enthält dieser Release zahlreiche neue Features wie die Szenario-editierten Massnahmen oder den überschriebenen Emissionsfaktoren. Ausserdem wurden die GIS Abfragen an die Kantone überarbeitet und verbessert.',
          changes: {
            additions: [
              'Import für Liegenschaften und Messwerte',
              'Hinweise für fehlende Baujahre bei den Gebäudehülle Komponenten',
              'Szenario editierter Massnahmen',
              'Aktiv von/bis der Liegenschaften mit Einfluss auf den Absenkpfad',
              'Emissionsfaktoren auf Liegenschaften',
              'Strombedarf Prozessanlagen',
            ],
            extensions: [
              'Tooltips und Hinweise zur Erläuterung von Datenquellen',
              'Effizienzklassen Kategorie im Reporting',
              'Jahre 2021 und 2022 bei den Heizgradtagen',
            ],
            upgrades: [
              'GIS Abfragen (Kantone: BS/LU/SH/SZ/TG/TI/UR/VD/VS)',
              'GIS Abfragen basierend auf Flächen statt Punkten (einige Kantone)',
              'Nutzungsdaten gemäss SIA 2024:2021',
            ],
            fixes: [],
          },
        },
      ],
    }
  },

  methods: {
    showReleaseNotesModal() {
      this.onModalOpen()
    },
  },
}
</script>

<style lang="scss" scoped>
.c-version-number {
  & .button {
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--spacing-xs);
    padding: 11px 16px;

    & > img {
      width: 16px;
      height: 16px;
    }
  }

  & h2 {
    margin: 0;
  }

  & .release-notes-modal {
    & .release-block {
      margin-bottom: var(--spacing-m);
    }

    & .change-type {
      display: flex;
      flex-direction: row;
      margin-bottom: 2px;

      & .change-type__title {
        min-width: 160px;
        margin-right: 40px;
      }
    }

    & .changes {
      list-style: disc;
      margin-bottom: 8px;

      & :not(:last-child) {
        margin-bottom: 2px;
      }
    }

    & .version-date {
      font-weight: 400;
      font-size: 14px;
      margin-bottom: var(--spacing-m);
    }

    & .version-summary {
      margin-bottom: var(--spacing-m);

      & ::v-deep .section {
        font-weight: 700;
      }

      & ::v-deep :not(h2) + .section {
        margin-top: 30px;
      }

      & ::v-deep p {
        margin: 10px 0 10px 0;
      }

      & ::v-deep ul {
        list-style-type: disc;
        list-style: disc outside none;
        padding: 0 0 0 24px;

        & ul {
          list-style-type: circle;
        }

        & li {
          padding: 0 0 0 2px;
          margin: 2px 0;
        }
      }

      & ::v-deep i {
        font-style: italic;
      }

      & ::v-deep td.keyword {
        width: 80px;
        font-style: italic;
      }
    }
  }
}
</style>
